import onLoad from "../../../javascripts/utils/onLoad";
import abort from '../../../javascripts/utils/abort';

type ContentPosition = {
  $target: Element,
  $link: HTMLAnchorElement,
  offset: number,
};

onLoad<HTMLElement>('.table-of-contents', ($tableOfContents) => {
  const targetsToLinks = new Map<Element, HTMLAnchorElement>();

  const $inner = $tableOfContents.querySelector('.table-of-contents__inner') ?? abort();
  const $scroller = $tableOfContents.querySelector<HTMLElement>('.table-of-contents__scroller') ?? abort();
  const $highlight = $tableOfContents.querySelector<HTMLElement>('.table-of-contents__highlight') ?? abort();
  const $leftNav = $tableOfContents.querySelector<HTMLButtonElement>('.table-of-contents__navigation[data-dir=left]') ?? abort();
  const $rightNav = $tableOfContents.querySelector<HTMLButtonElement>('.table-of-contents__navigation[data-dir=right]') ?? abort();

  let stuckQueued = false;
  let isStuck = false;

  const stuckCloseBy = 24;

  const setStuck = () => {
    if (stuckQueued) {
      return;
    }

    requestAnimationFrame(() => {
      stuckQueued = false;

      const { top } = $inner.getBoundingClientRect();
      const isNewlyStuck = top <= stuckCloseBy;

      if (isStuck !== isNewlyStuck) {
        isStuck = isNewlyStuck;

        $tableOfContents.classList.toggle('table-of-contents--stuck', isNewlyStuck);
      }
    });
  };

  setStuck();

  const getOffsetTop = ($element: Element): number => {
    let offset = 0;
    let $current: Element | null = $element;

    while ($current && ($current as HTMLElement).offsetParent) {
      offset += ($current as HTMLElement).offsetTop;
      $current = ($current as HTMLElement).offsetParent as Element;
    }

    return offset;
  };

  const getViewportPosition = (): number => {
    const scrollTop = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const nearBottom = scrollTop + windowHeight >= documentHeight - 50;
    const offset = 100;
    const dynamicOffset = nearBottom ? offset - 100 : offset;

    return scrollTop + dynamicOffset;
  };

  const getPositions = (): ContentPosition[] => Array.from(targetsToLinks.entries()).map(([$target, $link]) => ({
    $target,
    $link,
    offset: getOffsetTop($target),
  }));

  const getCurrentActive = (positions: ContentPosition[], position: number): ContentPosition | null => {
    const scrollTop = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const nearBottom = scrollTop + windowHeight >= documentHeight - 100;

    if (nearBottom) {
      return positions[positions.length - 1] || positions[0];
    }

    for (let i = positions.length - 1; i >= 0; i -= 1) {
      // eslint-disable-next-line security/detect-object-injection
      if (position >= positions[i].offset) {
        // eslint-disable-next-line security/detect-object-injection
        return positions[i];
      }
    }

    return null;
  }

  let timeout: number | null = null;
  let forceInProgress = false;

  const detect = ($force: HTMLAnchorElement | null = null) => {
    if (forceInProgress && !$force) {
      return;
    }

    if (timeout !== null) {
      forceInProgress = false;
      cancelAnimationFrame(timeout);
    }

    timeout = requestAnimationFrame(() => {
      const positions = getPositions();
      const position = getViewportPosition();

      const $firstLink = targetsToLinks.values().find(Boolean);
      const active = getCurrentActive(positions, position);
      const $link = $force ?? active?.$link ?? $firstLink;
      const visible = $force || active;

      if ($force) {
        forceInProgress = true;
        window.document.addEventListener('scrollend', () => { forceInProgress = false }, { once: true });
      }

      if ($link) {
        const { width, x } = $link.getBoundingClientRect();
        const insideX = x + $scroller.scrollLeft;

        $highlight.style.opacity = visible ? '1' : '0';
        $highlight.style.scale = `${visible ? width / 10 : 0} 1`;
        $highlight.style.translate = `${insideX}px 0px`;

        $scroller.scrollLeft = insideX - ($firstLink?.offsetLeft ?? 0);
      }
    });
  };

  $tableOfContents.querySelectorAll<HTMLAnchorElement>('.link').forEach(($link) => {
    const $target = $link.hash !== '' ? document.querySelector($link.hash) : null;

    if ($target) {
      targetsToLinks.set($target, $link);
      $link.addEventListener('click', () => { detect($link) });
    }
  });

  detect();

  const hasPointer = window.matchMedia("(hover: hover)");
  hasPointer.addEventListener('change', () => { detect() });

  $leftNav.addEventListener('click', () => {
    $scroller.scrollLeft -= $scroller.offsetWidth / 2;
  });

  $rightNav.addEventListener('click', () => {
    $scroller.scrollLeft += $scroller.offsetWidth / 2;
  });

  const updateNavigation = () => {
    const margin = 5;
    const isScrollable = $scroller.scrollWidth > $scroller.offsetWidth;

    $leftNav.disabled = $scroller.scrollLeft <= margin;
    $rightNav.disabled = $scroller.scrollWidth <= $scroller.scrollLeft + $scroller.offsetWidth + margin;

    $leftNav.style.opacity = isScrollable ? '1' : '0';
    $rightNav.style.opacity = isScrollable ? '1' : '0';
  };

  updateNavigation();

  $scroller.addEventListener('scroll', () => { updateNavigation() }, { passive: true });

  window.addEventListener('scroll', () => { detect(); setStuck(); }, { passive: true });
  window.addEventListener('resize', () => { detect(); updateNavigation(); setStuck(); }, { passive: true });
});
